<template>
  <div>
    <!-- Search and Filters -->
    <div class="bg-white/90 dark:bg-slate-800/90 border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-3 sm:p-4 mb-4 shadow-sm">
      <div class="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3">
        <!-- Search -->
        <div class="flex-1 min-w-0 sm:min-w-64 relative">
          <div class="relative group">
            <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-slate-600 dark:group-focus-within:text-slate-300 transition-colors" />
            <input
              v-model="search"
              type="text"
              placeholder="Name, Email, Telefon..."
              class="w-full pl-10 pr-4 py-2.5 bg-white/50 dark:bg-slate-900/50 border border-slate-200/50 dark:border-slate-600/50 rounded-xl focus:border-slate-400 focus:ring-2 focus:ring-slate-500/20 focus:bg-white dark:focus:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm placeholder-slate-400 transition-all"
              @input="debouncedSearch"
            >
          </div>
        </div>

        <!-- Clear Filters -->
        <button
          v-if="search"
          @click="clearFilters"
          class="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
          title="Filter zurücksetzen"
        >
          <X class="w-4 h-4" />
        </button>
      </div>
    </div>

    <!-- Results Count + Sort -->
    <div class="flex items-center justify-between mb-3 text-sm px-1">
      <div class="flex items-center gap-2 text-slate-600 dark:text-slate-400">
        <span class="font-medium text-slate-800 dark:text-slate-200">{{ displayedContacts.length }}</span>
        <span>Eigene Kontakte</span>
        <span v-if="myContactsLoading" class="flex items-center gap-1.5 text-blue-600">
          <Loader2 class="w-3.5 h-3.5 animate-spin" />
          <span class="text-xs">Laden...</span>
        </span>
      </div>
      
      <div class="flex items-center gap-2">
        <div class="flex items-center gap-0.5 bg-slate-100 dark:bg-slate-700 rounded-lg p-0.5">
          <button
            @click="changeSortOption('asc')"
            class="p-1.5 rounded-md transition-colors"
            :class="sortOption === 'asc' 
              ? 'bg-white dark:bg-slate-600 text-slate-800 dark:text-white shadow-sm' 
              : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'"
            title="A-Z sortieren"
          >
            <ArrowUpAZ class="w-4 h-4" />
          </button>
          <button
            @click="changeSortOption('desc')"
            class="p-1.5 rounded-md transition-colors"
            :class="sortOption === 'desc' 
              ? 'bg-white dark:bg-slate-600 text-slate-800 dark:text-white shadow-sm' 
              : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'"
            title="Z-A sortieren"
          >
            <ArrowDownAZ class="w-4 h-4" />
          </button>
          <button
            @click="changeSortOption('random')"
            class="p-1.5 rounded-md transition-colors"
            :class="sortOption === 'random' 
              ? 'bg-white dark:bg-slate-600 text-slate-800 dark:text-white shadow-sm' 
              : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'"
            title="Zufällig sortieren"
          >
            <Shuffle class="w-4 h-4" />
          </button>
        </div>
        
        <!-- Export CSV Button -->
        <button
          @click="exportContactsCSV"
          :disabled="isExporting"
          class="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
          title="Als CSV exportieren"
        >
          <Loader2 v-if="isExporting" class="w-3.5 h-3.5 animate-spin" />
          <Download v-else class="w-3.5 h-3.5" />
          <span class="hidden sm:inline">Export</span>
        </button>
      </div>
    </div>

    <!-- Contacts Grid -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
      <ContactCard
        v-for="contact in displayedContacts"
        :key="contact.email"
        :contact="contact"
        @click="$emit('select-contact', contact)"
      />
    </div>

    <!-- Empty State -->
    <div v-if="displayedContacts.length === 0 && !myContactsLoading" class="text-center py-12">
      <Users class="w-10 h-10 mx-auto mb-3 text-slate-300" />
      <p class="text-slate-500 dark:text-slate-400 mb-3">Keine eigenen Kontakte gefunden</p>
    </div>

    <!-- Initial Loading -->
    <div v-if="displayedContacts.length === 0 && myContactsLoading" class="text-center py-12">
      <Loader2 class="w-8 h-8 mx-auto mb-3 text-blue-500 animate-spin" />
      <p class="text-slate-500 text-sm">Lade Kontakte...</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onUnmounted } from 'vue';
import { Search, X, Users, Loader2, ArrowUpAZ, ArrowDownAZ, Shuffle, Download } from 'lucide-vue-next';
import { myContacts, myContactsLoading, fetchMyContacts } from '../websocketService';
import { branding } from '../oauth2-config';
import ContactCard from './ContactCard.vue';

const SORT_STORAGE_KEY = `${branding.storagePrefix}_sort_option`;

defineEmits(['select-contact']);

const search = ref('');
const sortOption = ref(loadSavedSort());
const isExporting = ref(false);

let searchTimeout = null;

function loadSavedSort() {
  try {
    return localStorage.getItem(SORT_STORAGE_KEY) || 'asc';
  } catch {
    return 'asc';
  }
}

function saveSortOption(option) {
  try {
    localStorage.setItem(SORT_STORAGE_KEY, option);
  } catch (e) {
    console.warn('Could not save sort option:', e);
  }
}

function changeSortOption(option) {
  sortOption.value = option;
  saveSortOption(option);
}

// Client-side sorted contacts
const displayedContacts = computed(() => {
  let result = [...myContacts.value];
  
  if (sortOption.value === 'asc') {
    result.sort((a, b) => (a.display_name || '').localeCompare(b.display_name || ''));
  } else if (sortOption.value === 'desc') {
    result.sort((a, b) => (b.display_name || '').localeCompare(a.display_name || ''));
  } else if (sortOption.value === 'random') {
    result.sort((a, b) => {
      const hashA = (a.email || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
      const hashB = (b.email || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
      const seed = Date.now() % 10000;
      return Math.sin(hashA * seed) - Math.sin(hashB * seed);
    });
  }
  
  return result;
});

const debouncedSearch = () => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    fetchMyContacts(search.value);
  }, 300);
};

const clearFilters = () => {
  search.value = '';
  fetchMyContacts();
};

// Export contacts to CSV
const exportContactsCSV = () => {
  if (isExporting.value || myContacts.value.length === 0) return;
  
  isExporting.value = true;
  try {
    const headers = ['Name', 'E-Mail', 'Position', 'Abteilung', 'Firma', 'Mobil', 'Telefon', 'Standort', 'Stadt'];
    const rows = displayedContacts.value.map(c => [
      c.display_name || '',
      c.email || '',
      c.job_title || '',
      c.department || '',
      c.company_name || '',
      c.mobile_phone || '',
      Array.isArray(c.business_phones) ? c.business_phones.join('; ') : '',
      c.office_location || '',
      c.city || ''
    ]);
    
    const BOM = '\uFEFF';
    const csvContent = BOM + [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${(cell || '').replace(/"/g, '""')}"`).join(';'))
    ].join('\r\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const dateStr = new Date().toISOString().slice(0, 10);
    link.download = `meine_kontakte_${dateStr}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Export failed:', error);
  } finally {
    isExporting.value = false;
  }
};

// Load my contacts on mount
fetchMyContacts();

onUnmounted(() => {
  clearTimeout(searchTimeout);
});
</script>
